// src/utils/http-service.js
import axios from 'axios'
import { HTTP_STATUS_CODE } from '@/constants/http-status'
import * as exception from '@/utils/exception'
import { ElLoading } from 'element-plus'
import { tenantId } from '@/utils/tenantUrl'
import { idempotent } from '@/utils/idempotentKeyService'

let loadingInstance = null
let requestCount = 0

const showLoading = () => {
  if (requestCount === 0 && !loadingInstance) {
    loadingInstance = ElLoading.service({
      fullscreen: false,
      text: '加载中...',
      background: 'rgba(0, 0, 0, 0.7)'
    })
  }
  requestCount++
}

const hideLoading = () => {
  requestCount--
  if (requestCount <= 0 && loadingInstance) {
    loadingInstance.close()
    loadingInstance = null
    requestCount = 0
  }
}

class HttpService {
  constructor() {
    this.instance = axios.create({
      baseURL: process.env.VUE_APP_API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // 请求拦截器
    this.instance.interceptors.request.use(
      config => {
        //showLoading()
        const token = sessionStorage.getItem('token')
        // 是否需要认证判断
        if (token) {
          showLoading()
          config.headers['X-Auth-Required'] = 'true'
          config.headers.Authorization = `Bearer ${token}`
        } else {
          config.headers['X-Auth-Required'] = 'false'
        }
        // tenantId追加
        if (tenantId){
          config.headers['X-Tenant'] = tenantId
        }
        // 幂等性key存在的场合，添加到header里
        if(idempotent.has()){
          config.headers['X-Idempotent-Key'] = idempotent.get()
        }

        return config
      },
      error => {
        hideLoading()
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.instance.interceptors.response.use(
      response => {
        // 成功&&幂等性key存在的场合，更新幂等性key
        if(idempotent.has()){
          idempotent.refresh()
        }
        hideLoading()
        return response.data.data
      },
      error => {
        // 失败&&幂等性key存在的场合，删除幂等性key
        if(idempotent.has()){
          idempotent.clear()
        }
        hideLoading()
        Object.keys(error).forEach(key => { console.log(key, error[key])})
        // 网络问题的情况
        if(error.code === 'ERR_NETWORK'){
          throw new exception.NetworkException()
        } else if(error.code === 'ECONNABORTED'){
          throw new exception.TimeoutException()
        }else if(error.response){
          const status = error.response.status
          const code = error.response.data?.error?.globalError?.code
          const message = error.response.data?.error?.globalError?.message
          console.log(status, 'status')
          console.log(code, 'code')
          console.log(message, 'message')

          switch(status){
            case HTTP_STATUS_CODE.BAD_REQUEST:
              throw new exception.BussinessException(code, message)
            case HTTP_STATUS_CODE.UNAUTHORIZED:
              throw new exception.AuthenticationException(message)
            case HTTP_STATUS_CODE.FORBIDDEN:
              throw new exception.ForbiddenException
            case HTTP_STATUS_CODE.NOT_FOUND:
              throw new exception.NotFoundException
            case HTTP_STATUS_CODE.CONFLICT:
              throw new exception.IdempotentException
            default:
              throw new exception.SystemException
          }
        } 
      }
    )
  }
  
  // GET 请求
  get(url, params = {}, config = {}) {
    return this.instance.get(url, { params, ...config })
  }

  // POST 请求
  post(url, data = {}, config = {}) {
    return this.instance.post(url, data, config)
  }

  // 上传文件
  upload(url, file, onProgress) {
    const formData = new FormData()
    formData.append('file', file)

    return this.instance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress
    })
  }

  // 下载文件
  download(url, filename) {
    return this.instance.get(url, {
      responseType: 'blob'
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
    })
  }

  // 设置全局 token
  setToken(token) {
    this.instance.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  // 移除 token
  removeToken() {
    delete this.instance.defaults.headers.common['Authorization']
  }
}

// 创建单例
const httpService = new HttpService()
export default httpService